// @src/pre-deployment/src/components/ChapterCompletion.js v1.2
// - Guard modulesOrder/localModulesOrder .map (avoid .map on undefined per ErrorHandling)

import React, { useState, useEffect } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { ChapterProgress } from '../../data/tracking';
import useDataService from '../../data/useDataService';
import { TrackingV2Service } from '../../tracking/trackingService.js';

const ChapterCompletion = ({ _moduleKey, _moduleConfig, navigation, modulesOrder, _moduleMap, level, chapter }) => {
    const { user } = useAuth();

    const [completionData, setCompletionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localModulesOrder, setLocalModulesOrder] = useState(['dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle', 'complete']);

    // Fetch session progress data using new tracking system
    const {
        data: progressData,
        loading: progressLoading,
        error: progressError
    } = useDataService.useLearningProgressV2(user?.uid, parseInt(level), parseInt(chapter));

    // Get LearningModulesOrder
    const {
        data: modulesOrderData,
        loading: modulesOrderLoading,
        error: modulesOrderError
    } = useDataService.useLearningModulesOrder(parseInt(level), parseInt(chapter));

    useEffect(() => {
        const fetchProgressData = async () => {
            if (!user?.uid) {
                setLoading(false);
                return;
            }

            try {
                // First, get the modules order from LearningModulesOrder
                console.log('📋 ChapterCompletion: Using modules order from hook');
                if (modulesOrderData && Array.isArray(modulesOrderData)) {
                    setLocalModulesOrder(modulesOrderData);
                    console.log('📋 Modules order:', modulesOrderData);
                }

                // Use progressData from the hook instead of calling getLearningProgress
                console.log('📊 ChapterCompletion: Progress data from hook:', progressData);

                if (progressData && progressData.learningProgress) {
                    console.log('📊 ChapterCompletion: Progress data loaded with enhanced scoring');
                    console.log('Progress data:', progressData);
                    console.log('Data path: users/{userId}/level_{level}/chapter_{chapter}/{sessionType}/LearningProgress');

                    // FIXED: Calculate module performance metrics using corrected data structure
                    const moduleScores = {};
                    const learningProgress = progressData.learningProgress;
                    if (learningProgress && learningProgress.modules) {
                        Object.keys(learningProgress.modules).forEach(moduleType => {
                            const module = learningProgress.modules[moduleType];

                            if (module.completed_exercises > 0) {
                                // FIXED: Use the corrected metrics from DataService
                                moduleScores[moduleType] = {
                                    // Completion percentage (capped at 100%)
                                    completion: Math.min(100, Math.round((module.completed_exercises / module.total_exercises) * 100)),
                                    // FIXED: Use accuracy_rate directly (already calculated correctly in DataService)
                                    accuracy: Math.min(100, module.accuracy_rate || 0),
                                    // FIXED: Use average_score directly (already calculated correctly in DataService)
                                    averageScore: Math.min(100, module.average_score || 0),
                                    // Time metrics
                                    timeSpent: module.total_time_spent || 0,
                                    avgTimePerExercise: module.avg_time_per_exercise || 0,
                                    // Exercise counts
                                    exercisesCompleted: module.completed_exercises || 0,
                                    totalExercises: module.total_exercises || 0,
                                    // Additional metrics
                                    hintsUsed: module.hints_used_total || 0,
                                    avgHintsPerExercise: module.avg_hints_per_exercise || 0,
                                    correctAttempts: module.correct_attempts || 0
                                };
                            } else {
                                // Default values for modules with no completed exercises
                                moduleScores[moduleType] = {
                                    completion: 0,
                                    accuracy: 0,
                                    averageScore: 0,
                                    timeSpent: 0,
                                    avgTimePerExercise: 0,
                                    exercisesCompleted: 0,
                                    totalExercises: module.total_exercises || 0,
                                    hintsUsed: 0,
                                    avgHintsPerExercise: 0,
                                    correctAttempts: 0
                                };
                            }
                        });
                    }

                    // FIXED: Calculate overall performance metrics using corrected data
                    const overallAccuracy = learningProgress.overall_accuracy_rate !== undefined ?
                        Math.min(100, Math.round(learningProgress.overall_accuracy_rate)) : 0;

                    // FIXED: Use overall accuracy as average score (they should be the same)
                    const overallAverageScore = overallAccuracy;

                    setCompletionData({
                        level: level,
                        chapter: chapter,
                        totalExercises: learningProgress.total_exercises || 0,
                        completedExercises: learningProgress.completed_exercises || 0,
                        averageScore: overallAverageScore,
                        overallAccuracy: overallAccuracy,
                        timeSpent: learningProgress.total_time_spent || 0,
                        lastCompleted: learningProgress.last_updated || null,
                        moduleScores: {
                            dialogue: moduleScores.dialogue || {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            },
                            vocabulary: moduleScores.vocabulary || {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            },
                            translation: moduleScores.translation || {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            },
                            grammar: moduleScores.grammar || {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            },
                            cloze: moduleScores.cloze || {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            },
                            shapePuzzle: moduleScores.shapePuzzle || {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            }
                        }
                    });
                } else {
                    // No data found, create empty completion data with enhanced structure
                    setCompletionData({
                        level: level,
                        chapter: chapter,
                        totalExercises: 0,
                        completedExercises: 0,
                        averageScore: 0,
                        overallAccuracy: 0,
                        timeSpent: 0,
                        lastCompleted: null,
                        moduleScores: {
                            dialogue: {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            },
                            vocabulary: {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            },
                            translation: {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            },
                            grammar: {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            },
                            cloze: {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            },
                            shapePuzzle: {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            }
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching progress data:', error);
                // Set empty data on error with enhanced structure
                setCompletionData({
                    level: level,
                    chapter: chapter,
                    totalExercises: 0,
                    completedExercises: 0,
                    averageScore: 0,
                    overallAccuracy: 0,
                    timeSpent: 0,
                    lastCompleted: null,
                    moduleScores: {
                        dialogue: {
                            completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                            avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                            hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                        },
                        vocabulary: {
                            completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                            avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                            hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                        },
                        translation: {
                            completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                            avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                            hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                        },
                        grammar: {
                            completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                            avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                            hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                        },
                        cloze: {
                            completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                            avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                            hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                        },
                        shapePuzzle: {
                            completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                            avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                            hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                        }
                    }
                });
            }
            setLoading(false);
        };

        fetchProgressData();
    }, [user?.uid, level, chapter, progressData, modulesOrderData]);

    // Mark chapter as completed on mount
    // Mark chapter as completed when component mounts with new tracking system
    useEffect(() => {
        const markChapterCompleted = async () => {
            if (user?.uid && level && chapter) {
                try {
                    // Mark chapter as completed with new tracking system
                    await TrackingV2Service.markChapterCompleted(user.uid, level, chapter, {
                        completionMethod: 'user_reached_completion_screen',
                        completedAt: new Date().toISOString(),
                        completionMetrics: {
                            totalModules: localModulesOrder.length,
                            completedModules: localModulesOrder.length - 1, // All except completion screen
                            completionRate: 100
                        }
                    });

                    // Mark completion module as started and completed
                    await TrackingV2Service.updateModuleProgress(user.uid, level, chapter, 'complete', {
                        userStarted: true,
                        startDate: new Date().toISOString(),
                        completion: true,
                        completionTime: new Date().toISOString(),
                        lastOpened: new Date().toISOString()
                    }, {
                        moduleType: 'complete',
                        interactionType: 'completion_screen',
                        completionMethod: 'user_reached_completion_screen'
                    });

                    console.log('✅ Chapter completion tracked successfully');

                } catch (error) {
                    console.error('❌ Failed to track chapter completion:', error);

                    // Fallback to old tracking system
                    ChapterProgress.updateModule(user.uid, level, chapter, 'complete', {
                        user_started: true,
                        start_date: new Date().toISOString(),
                        completion: true,
                        completion_time: new Date().toISOString()
                    });
                    ChapterProgress.markCompleted(user.uid, level, chapter);
                }
            }
        };

        markChapterCompleted();
    }, [user?.uid, level, chapter, localModulesOrder.length]);

    const handleReturnToMenu = () => navigation?.menu();
    const handleNextChapter = () => navigation?.next();

    const formatTime = (seconds) => {
        if (!seconds) return '0m';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#28a745';
        if (score >= 60) return '#ffc107';
        return '#dc3545';
    };

    const getScoreLabel = (score) => {
        if (score >= 80) return 'Excellent';
        if (score >= 60) return 'Good';
        if (score > 0) return 'Needs Practice';
        return 'Not Attempted';
    };

    const getModuleDisplayName = (moduleType) => {
        const displayNames = {
            'dialogue': 'Dialogue Practice',
            'vocabulary': 'Vocabulary Practice',
            'translation': 'Translation Matching',
            'grammar': 'Grammar Module',
            'cloze': 'Cloze Exercise',
            'shapePuzzle': 'Shape Puzzle'
        };
        return displayNames[moduleType] || moduleType.charAt(0).toUpperCase() + moduleType.slice(1);
    };

    // Early returns for loading and error states - moved after all hooks
    if (loading || progressLoading || modulesOrderLoading) {
        return (
            <div className="chapter-completion-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading completion data...</p>
                </div>
            </div>
        );
    }

    if (progressError || modulesOrderError) {
        return (
            <div className="chapter-completion-container">
                <div className="error-container">
                    <h3>❌ Error Loading Progress</h3>
                    <p>{progressError?.message || modulesOrderError?.message || 'Unknown error'}</p>
                    <button onClick={handleReturnToMenu}>Return to Menu</button>
                </div>
            </div>
        );
    }

    return (
        <div className="chapter-completion-container">
            <style jsx>{`
                .score-item.detailed {
                    padding: 15px;
                    border: 1px solid #e1e5e9;
                    border-radius: 8px;
                    margin-bottom: 15px;
                    background: #f8f9fa;
                    transition: all 0.3s ease;
                }
                
                .score-item.detailed:hover {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    transform: translateY(-2px);
                }
                
                .score-item.detailed .module-name {
                    margin: 0 0 10px 0;
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #2c3e50;
                    border-bottom: 2px solid #3498db;
                    padding-bottom: 5px;
                }
                
                .score-details {
                    margin-top: 10px;
                }
                
                .score-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                    font-size: 0.9rem;
                    padding: 4px 0;
                }
                
                .score-row:last-child {
                    margin-bottom: 0;
                }
                
                .score-row span:first-child {
                    font-weight: 500;
                    color: #666;
                    flex: 1;
                }
                
                .score-row .score-value {
                    font-weight: 600;
                    color: #2c3e50;
                    min-width: 60px;
                    text-align: right;
                }
                
                .scores-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 15px;
                    margin-top: 20px;
                }
                
                .completion-stats {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 20px;
                    margin: 30px 0;
                }
                
                .stat-card {
                    background: linear-gradient(135deg, #FAF1CA 0%, #FAF1CA 100%);
                    color: white;
                    padding: 20px;
                    border-radius: 12px;
                    text-align: center;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                    transition: transform 0.3s ease;
                }
                
                .stat-card:hover {
                    transform: translateY(-5px);
                }
                
                .stat-card h3 {
                    margin: 0 0 10px 0;
                    font-size: 0.9rem;
                    opacity: 0.9;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }
                
                .stat-card .stat-value {
                    font-size: 2rem;
                    font-weight: bold;
                    margin: 10px 0;
                }
                
                .stat-card .stat-label {
                    font-size: 0.8rem;
                    opacity: 0.8;
                }
                
                @media (max-width: 768px) {
                    .scores-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .completion-stats {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    
                    .score-item.detailed {
                        padding: 12px;
                    }
                }
            `}</style>

            <div className="completion-content">
                <div className="completion-header">
                    <h1>Chapter {chapter} Complete!</h1>
                    <p>Congratulations on completing Level {level}, Chapter {chapter}</p>
                </div>

                <div className="completion-stats">
                    <div className="stat-card">
                        <h3>Overall Progress</h3>
                        <div className="stat-value">{completionData.completedExercises}/{completionData.totalExercises}</div>
                        <div className="stat-label">Exercises Completed</div>
                    </div>

                    <div className="stat-card">
                        <h3>Overall Accuracy</h3>
                        <div className="stat-value" style={{ color: getScoreColor(completionData.overallAccuracy) }}>
                            {completionData.overallAccuracy}%
                        </div>
                        <div className="stat-label">{getScoreLabel(completionData.overallAccuracy)}</div>
                    </div>

                    <div className="stat-card">
                        <h3>Average Score</h3>
                        <div className="stat-value" style={{ color: getScoreColor(completionData.averageScore) }}>
                            {completionData.averageScore}%
                        </div>
                        <div className="stat-label">{getScoreLabel(completionData.averageScore)}</div>
                    </div>

                    <div className="stat-card">
                        <h3>Time Spent</h3>
                        <div className="stat-value">{formatTime(completionData.timeSpent)}</div>
                        <div className="stat-label">Total Study Time</div>
                    </div>
                </div>

                <div className="module-scores">
                    <h2>Performance Metrics</h2>
                    <div className="scores-grid">
                        {(Array.isArray(modulesOrder) ? modulesOrder : Array.isArray(localModulesOrder) ? localModulesOrder : []).map((moduleType) => {
                            const moduleName = getModuleDisplayName(moduleType);
                            const scores = completionData.moduleScores[moduleType] || {
                                completion: 0, accuracy: 0, averageScore: 0, timeSpent: 0,
                                avgTimePerExercise: 0, exercisesCompleted: 0, totalExercises: 0,
                                hintsUsed: 0, avgHintsPerExercise: 0, correctAttempts: 0
                            };

                            return (
                                <div key={moduleType} className="score-item detailed">
                                    <h4 className="module-name">{moduleName}</h4>
                                    <div className="score-details">
                                        <div className="score-row">
                                            <span>Completion:</span>
                                            <span className="score-value">{scores.completion}%</span>
                                        </div>
                                        <div className="score-row">
                                            <span>Accuracy:</span>
                                            <span className="score-value" style={{ color: getScoreColor(scores.accuracy) }}>
                                                {scores.accuracy}%
                                            </span>
                                        </div>
                                        <div className="score-row">
                                            <span>Avg Score:</span>
                                            <span className="score-value" style={{ color: getScoreColor(scores.averageScore) }}>
                                                {scores.averageScore}%
                                            </span>
                                        </div>
                                        <div className="score-row">
                                            <span>Time/Exercise:</span>
                                            <span className="score-value">{formatTime(scores.avgTimePerExercise)}</span>
                                        </div>
                                        <div className="score-row">
                                            <span>Exercises:</span>
                                            <span className="score-value">{scores.exercisesCompleted}/{scores.totalExercises}</span>
                                        </div>
                                        {scores.hintsUsed > 0 && (
                                            <div className="score-row">
                                                <span>Hints Used:</span>
                                                <span className="score-value">{scores.hintsUsed}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="completion-actions">
                    <button className="action-btn primary" onClick={handleReturnToMenu}>
                        <i className="fas fa-home"></i>
                        Return to Main Menu
                    </button>

                    {parseInt(chapter) < 10 ? (
                        <button className="action-btn secondary" onClick={handleNextChapter}>
                            <i className="fas fa-arrow-right"></i>
                            Continue to Chapter {parseInt(chapter) + 1}
                        </button>
                    ) : (
                        <button className="action-btn secondary" onClick={() => {
                            // Navigate to next level
                            if (navigation && navigation.navigate) {
                                navigation.navigate(`/level/${parseInt(level) + 1}/chapter/1`);
                            } else {
                                window.location.href = `/level/${parseInt(level) + 1}/chapter/1`;
                            }
                        }}>
                            <i className="fas fa-trophy"></i>
                            Start Level {parseInt(level) + 1}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChapterCompletion;
